import { InfoWithLabel } from "@/core/components/info-with-label";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApPurchaseDocument } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  hasManualPurchaseOrder,
  stPurchaseOrderUrl,
} from "@/accounts-payable/utils";
import { PurchaseOrderMatchButton } from "./purchase-order-match";

/**
 * The ServiceTitan PO a document's cost sits on, beside the action to choose
 * it by hand. Its own row under the job, which a PO choice can move.
 */
export const PurchaseOrderField = ({
  document,
  businessId,
}: {
  document: ApPurchaseDocument;
  businessId: string | null;
}) => {
  const poId = document.st_po_external_id ?? null;
  const manual = hasManualPurchaseOrder(document);

  return (
    <Stack direction="row" justify="between" align="end" gap="md" fullWidth>
      <InfoWithLabel label="Purchase order" fullWidth={false}>
        {poId ? (
          <Stack direction="row" gap="sm" align="center" className="flex-wrap">
            {/* Same link styling as the job above it. */}
            <ServiceTitanIconLink
              url={stPurchaseOrderUrl(poId)}
              label={`Open purchase order #${poId} in ServiceTitan`}
              className="ml-0 text-lg text-interactive-text hover:text-interactive-text hover:underline"
            >
              #{poId}
            </ServiceTitanIconLink>
            {manual && (
              <Typography variant="bodyW1" textColor="tertiary">
                (chosen by hand)
              </Typography>
            )}
          </Stack>
        ) : manual ? (
          <Stack direction="row" gap="sm" align="center">
            <Typography variant="bodyW1">No existing PO</Typography>
            <Typography variant="bodyW1" textColor="tertiary">
              (set by hand)
            </Typography>
          </Stack>
        ) : (
          <Typography variant="bodyW1" textColor="tertiary">
            None recorded
          </Typography>
        )}
      </InfoWithLabel>
      {canManuallyMatchJob(document) && (
        <PurchaseOrderMatchButton
          businessId={businessId}
          documentId={document.id}
          documentType={document.document_type}
          agentRunId={document.agent_run_id}
          hasJob={document.st_job_external_id != null}
          label={poId ? "Change PO" : undefined}
        />
      )}
    </Stack>
  );
};
